/**
 * Search module
 * Handles the Search tab functionality with filtering, sorting, and URL-based routing
 */

import { API } from '../core/api.js';
import { debounce, escapeHtml, escapeRegex } from '../core/utils.js';
import { showNotification } from '../components/notifications.js';
import { setEpisodeSearchQuery } from './episode-view.js';

// Store podcasts list for filter dropdown
let podcastsList = [];

/**
 * Initialize search with filter controls
 */
export async function initSearch() {
    // Load podcasts for filter dropdown
    try {
        const data = await API.get('/api/podcasts');
        // Handle both array and object responses
        podcastsList = Array.isArray(data) ? data : (data.podcasts || []);
        renderSearchFilters();
    } catch (error) {
        console.error('Error loading podcasts:', error);
    }

    setupSearch();

    // Check URL for search params and execute search if present
    handleSearchFromURL();
}

/**
 * Handle search parameters from URL hash
 */
export function handleSearchFromURL() {
    const hash = window.location.hash;
    if (!hash.startsWith('#/search')) return;

    // Parse query params from hash: #/search?q=term&sort=newest
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(queryString);

    const query = params.get('q');
    const podcastId = params.get('podcast_id');
    const sort = params.get('sort');
    const contentType = params.get('content_type');

    // Populate search fields
    const searchInput = document.getElementById('search-input');
    if (searchInput && query) {
        searchInput.value = query;
    }

    // Set filter values if they exist
    if (podcastId) {
        const podcastSelect = document.getElementById('filter-podcast');
        if (podcastSelect) podcastSelect.value = podcastId;
    }
    if (sort) {
        const sortSelect = document.getElementById('filter-sort');
        if (sortSelect) sortSelect.value = sort;
    }
    if (contentType) {
        const contentTypeSelect = document.getElementById('filter-content-type');
        if (contentTypeSelect) contentTypeSelect.value = contentType;
    }

    // Execute search if query present
    if (query) {
        performSearch(false); // Don't update URL since we're loading from it
    }
}

/**
 * Update URL with current search state
 */
function updateSearchURL(query, podcastId, sort, contentType, status) {
    if (!query) return;

    const params = new URLSearchParams();
    params.set('q', query);
    if (podcastId) params.set('podcast_id', podcastId);
    if (sort && sort !== 'relevance') params.set('sort', sort);
    if (contentType) params.set('content_type', contentType);
    if (status) params.set('status', status);

    const newHash = `#/search?${params.toString()}`;
    if (window.location.hash !== newHash) {
        history.pushState(null, '', newHash);
    }
}

/**
 * Render search filter controls
 */
function renderSearchFilters() {
    const searchContainer = document.querySelector('.search-container');
    if (!searchContainer) return;

    // Check if filters already exist
    if (document.getElementById('search-filters')) return;

    // Create filters row
    const filtersHtml = `
        <div id="search-filters" class="search-filters">
            <select id="filter-podcast" class="search-filter-select">
                <option value="">All Podcasts</option>
                ${podcastsList.map(p => `<option value="${p.id}">${escapeHtml(p.title)}</option>`).join('')}
            </select>
            <select id="search-filter-status" class="search-filter-select">
                <option value="">All Episodes</option>
                <option value="unplayed">Unplayed</option>
                <option value="played">Played</option>
            </select>
            <select id="filter-sort" class="search-filter-select">
                <option value="relevance">Sort: Relevance</option>
                <option value="newest">Sort: Newest First</option>
                <option value="oldest">Sort: Oldest First</option>
            </select>
            <select id="filter-content-type" class="search-filter-select">
                <option value="">All Content</option>
                <option value="title">Title Only</option>
                <option value="description">Description Only</option>
                <option value="transcription">Transcription Only</option>
                <option value="keywords">Keywords Only</option>
                <option value="entities">Entities Only</option>
            </select>
        </div>
    `;

    // Insert after search input
    const searchInput = searchContainer.querySelector('#search-input');
    if (searchInput) {
        searchInput.insertAdjacentHTML('afterend', filtersHtml);

        // Add change listeners to filters
        document.getElementById('filter-podcast')?.addEventListener('change', () => performSearch(true));
        document.getElementById('filter-sort')?.addEventListener('change', () => performSearch(true));
        document.getElementById('filter-content-type')?.addEventListener('change', () => performSearch(true));
    }
}

/**
 * Perform search with current filters
 * @param {boolean} updateURL - Whether to update the URL hash (default true)
 */
export async function performSearch(updateURL = true) {
    const query = document.getElementById('search-input')?.value?.trim();
    const resultsContainer = document.getElementById('search-results');

    if (!resultsContainer) return;

    if (!query) {
        resultsContainer.innerHTML = '<p style="color: var(--text-secondary); text-align: center;">Enter a search term to find episodes.</p>';
        return;
    }

    // Show loading state
    resultsContainer.innerHTML = '<p style="color: var(--text-secondary); text-align: center;"><span class="loading"></span> Searching...</p>';

    try {
        // Get filter values
        const podcastId = document.getElementById('filter-podcast')?.value || '';
        const sort = document.getElementById('filter-sort')?.value || 'relevance';
        const contentType = document.getElementById('filter-content-type')?.value || '';
        const status = document.getElementById('search-filter-status')?.value || '';

        // Update URL if requested
        if (updateURL) {
            updateSearchURL(query, podcastId, sort, contentType, status);
        }

        // Build query params
        let queryParams = `q=${encodeURIComponent(query)}`;
        if (podcastId) queryParams += `&podcast_id=${podcastId}`;
        if (sort) queryParams += `&sort=${sort}`;
        if (contentType) queryParams += `&content_type=${contentType}`;
        if (status) queryParams += `&status=${status}`;

        const data = await API.get(`/api/search/episodes?${queryParams}`);

        if (!data.results || data.results.length === 0) {
            resultsContainer.innerHTML = `
                <p style="color: var(--text-secondary); text-align: center;">
                    No results found for "${escapeHtml(query)}".
                </p>
            `;
            return;
        }

        // Escape the query for use in onclick handlers
        const escapedQuery = query.replace(/'/g, "\\'").replace(/"/g, '\\"');

        // Render results
        const headerHtml = `<p style="margin-bottom: 1rem; color: var(--text-secondary);">Found ${data.total || data.results.length} result${data.results.length !== 1 ? 's' : ''} for "${escapeHtml(query)}"</p>`;

        const resultsHtml = data.results.map(result => {
            const snippetHtml = result.snippet
                ? `<div style="margin-top: 0.5rem; color: var(--text-secondary); font-size: 0.9rem; line-height: 1.5;">...${safeSnippet(result.snippet)}...</div>`
                : '';

            return `<div class="episode-item search-result" data-episode-id="${result.episode_id}" data-query="${escapedQuery}" style="cursor: pointer;">
                <div class="episode-header">
                    <div>
                        <div class="episode-title">${highlightMatch(result.title, query)}</div>
                        <div style="color: var(--text-secondary); font-size: 0.85rem; margin-top: 0.25rem;">${escapeHtml(result.podcast_title) || 'Unknown podcast'}</div>
                    </div>
                    <div class="episode-actions">
                        <button class="btn btn-small btn-secondary search-result-view-btn">View</button>
                    </div>
                </div>
                ${snippetHtml}
                <div class="episode-meta">
                    <span>${result.published_date ? new Date(result.published_date).toLocaleDateString() : 'Unknown date'}</span>
                    ${result.match_count ? `<span>${result.match_count} matches</span>` : ''}
                </div>
            </div>`;
        }).join('');

        resultsContainer.textContent = '';
        resultsContainer.insertAdjacentHTML('beforeend', headerHtml + resultsHtml);

        // Add click handlers using event delegation
        resultsContainer.querySelectorAll('.search-result').forEach(el => {
            const episodeId = parseInt(el.dataset.episodeId);
            const searchQuery = el.dataset.query;
            el.addEventListener('click', () => openEpisodeWithSearch(episodeId, searchQuery));
            el.querySelector('.search-result-view-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                openEpisodeWithSearch(episodeId, searchQuery);
            });
        });

    } catch (error) {
        console.error('Error performing search:', error);
        resultsContainer.innerHTML = `
            <p style="color: var(--error); text-align: center;">
                Error performing search. Please try again.
            </p>
        `;
    }
}

/**
 * Highlight matching text in search results
 * Escapes HTML first to prevent XSS, then applies highlighting
 */
function highlightMatch(text, query) {
    if (!text || !query) return escapeHtml(text) || '';

    // First escape HTML to prevent XSS
    const escapedText = escapeHtml(text);
    const escapedQuery = escapeHtml(query);

    // Then apply highlighting on the escaped text
    const regex = new RegExp(`(${escapeRegex(escapedQuery)})`, 'gi');
    return escapedText.replace(regex, '<mark>$1</mark>');
}

/**
 * Safely render a search snippet with FTS5 highlight marks
 * Escapes HTML but preserves legitimate <mark> tags from FTS5
 */
function safeSnippet(snippet) {
    if (!snippet) return '';

    // Temporarily replace FTS5 <mark> tags with placeholders
    const markOpen = '\x00MARK_OPEN\x00';
    const markClose = '\x00MARK_CLOSE\x00';

    let safe = snippet
        .replace(/<mark>/gi, markOpen)
        .replace(/<\/mark>/gi, markClose);

    // Escape all HTML
    safe = escapeHtml(safe);

    // Restore the mark tags
    safe = safe
        .replace(new RegExp(markOpen, 'g'), '<mark>')
        .replace(new RegExp(markClose, 'g'), '</mark>');

    return safe;
}

/**
 * Setup search input with debouncing
 */
export function setupSearch() {
    const searchInput = document.getElementById('search-input');
    if (searchInput) {
        searchInput.addEventListener('input', debounce(() => performSearch(true), 300));
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                performSearch(true);
            }
        });
    }
}

/**
 * Open episode with search query pre-filled
 * @param {number} episodeId - The episode ID
 * @param {string} query - The search query to highlight
 */
export function openEpisodeWithSearch(episodeId, query) {
    // Set the search query before opening the episode
    setEpisodeSearchQuery(query);
    // Use the global viewEpisodeDetails function
    window.viewEpisodeDetails(episodeId);
}
